import { z } from 'zod';
import { weddingSiteSchema } from './site.js';
import {
  auditFields,
  centsSchema,
  currencySchema,
  isoDateSchema,
  optionalText,
  shortText,
  slugSchema,
  ulidSchema,
} from '../common.js';

export const weddingSettingsSchema = z.object({
  /** Shown on the Home tab and drives the checklist template. */
  guestTarget: z.number().int().min(0).max(5000).optional(),
  budgetTotalCents: centsSchema.optional(),
  currency: currencySchema,
  timezone: z.string().max(64).default('America/New_York'),
  locale: z.enum(['en', 'es']).default('en'),
});
export type WeddingSettings = z.infer<typeof weddingSettingsSchema>;

export const weddingSchema = z.object({
  type: z.literal('Wedding'),
  wedId: ulidSchema,
  wsId: ulidSchema,
  /** Display names of the couple, e.g. "David & Nicole". Shown in Cormorant. */
  title: shortText,
  partnerOneName: optionalText,
  partnerTwoName: optionalText,
  /** Nullable until the couple picks a date — the whole timeline hangs off it. */
  date: isoDateSchema.optional(),
  city: optionalText,
  slug: slugSchema.optional(),
  sitePublished: z.boolean().default(false),
  /**
   * What the public page shows. Lives on the wedding rather than in its own item
   * because it is read exactly when the wedding is, is edited as one document,
   * and is a few kilobytes of prose.
   */
  site: weddingSiteSchema.default({}),
  settings: weddingSettingsSchema,
  ...auditFields,
});
export type Wedding = z.infer<typeof weddingSchema>;

export const createWeddingSchema = z.object({
  title: shortText,
  partnerOneName: optionalText,
  partnerTwoName: optionalText,
  date: isoDateSchema.optional(),
  city: optionalText,
  settings: weddingSettingsSchema.partial().optional(),
});
export type CreateWeddingInput = z.infer<typeof createWeddingSchema>;

export const updateWeddingSchema = createWeddingSchema.partial().extend({
  slug: slugSchema.optional(),
  sitePublished: z.boolean().optional(),
});
export type UpdateWeddingInput = z.infer<typeof updateWeddingSchema>;

/** Headline numbers for the Home tab. Computed server-side, never trusted from the client. */
export const weddingSummarySchema = z.object({
  wedId: ulidSchema,
  daysToWedding: z.number().int().nullable(),
  guests: z.object({
    invited: z.number().int(),
    attending: z.number().int(),
    declined: z.number().int(),
    pending: z.number().int(),
    maybe: z.number().int(),
    households: z.number().int(),
    /** Said yes. */
    confirmedHeadcount: z.number().int(),
    /** Attending, maybe and pending — what the venue has to hold. */
    anticipatedHeadcount: z.number().int(),
    /** The same, minus whoever the couple has marked unlikely to come. */
    expectedHeadcount: z.number().int(),
  }),
  budget: z.object({
    plannedCents: centsSchema,
    projectedCents: centsSchema,
    paidCents: centsSchema,
    currency: z.string(),
  }),
  venues: z.object({
    total: z.number().int(),
    shortlisted: z.number().int(),
    booked: z.number().int(),
  }),
  vendors: z.object({ total: z.number().int(), booked: z.number().int() }),
  /** The checklist, as the Home tab needs it: a count and the next few things. */
  tasks: z.object({
    open: z.number().int(),
    overdue: z.number().int(),
    next: z
      .array(
        z.object({
          taskId: z.string(),
          title: z.string(),
          dueDate: isoDateSchema.optional(),
          overdue: z.boolean(),
        }),
      )
      .max(3),
  }),
});
export type WeddingSummary = z.infer<typeof weddingSummarySchema>;
